import { useGetVideoTranslations } from '@vimeo/gctl-api/hooks/useVideoTranslations';
import type {
  VideoTranslation,
  VideoTranslationFields,
} from '@vimeo/gctl-api/models/components/schemas/video-translation/VideoTranslation';
import { usePoll } from '@vimeo/ui/hooks/use-poll';

import { mapVideoIdTypeForGCTL } from '../utils/mapVideoIdTypeForGCTL';

const TEXT_STATUSES = {
  DONE: 'DONE',
  FAILED: 'FAILED',
  PROCESS: 'PRCS',
  NONE: 'NONE_EXISTING',
};

const POLLING_INTERVAL = 3000;

const VIDEO_TRANSLATION_FIELDS: VideoTranslationFields[] = [
  'serviceMode',
  'status',
  'targetLang',
  'audiotrackUri',
  'translationMode',
];

type Props = {
  videoId?: string;
  hasVideoTranslations?: boolean;
  hasVideoTextTranslations?: boolean;
};
export function useClipTranslations({ videoId, hasVideoTranslations }: Props): {
  translations?: VideoTranslation[];
  keepPollingTranslations?: boolean;
  isFetching: boolean;
} {
  const {
    data: translationsFetchResult,
    mutate: getTranslations,
    isLoading,
    isValidating,
  } = useGetVideoTranslations(() => {
    if (!videoId || !hasVideoTranslations) {
      return null;
    }

    return {
      where: mapVideoIdTypeForGCTL(videoId),
      select: VIDEO_TRANSLATION_FIELDS,
    };
  });

  // this condition tells us that AI-generated translation track will be available shortly
  const keepPollingTranslations = Boolean(
    translationsFetchResult?.data &&
      translationsFetchResult?.data.some(
        (translation) =>
          translation.serviceMode === 'full_apply' && translation.status === TEXT_STATUSES.PROCESS,
      ),
  );

  usePoll(getTranslations, keepPollingTranslations, {
    interval: POLLING_INTERVAL,
    backoff: 'linear',
    shouldResetBackoff: (requestCount: number) => requestCount > 5,
  });

  const audioTranslations = translationsFetchResult?.data.filter(
    (tr: VideoTranslation) => tr.translationMode === 'dubbing',
  );
  return {
    translations: audioTranslations,
    keepPollingTranslations,
    isFetching: isLoading || isValidating,
  };
}
